import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Modal, Upload } from 'antd'
import type { ExpenseImportRow } from '@super-mega/server/contracts/expense'
import { useTRPC } from '@/trpc/react.ts'
import { downloadExpenseTemplate, readExpenseFile } from './expense-import-file.ts'

export function ExpenseImport({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<unknown> }) {
  const trpc = useTRPC(),
    { message } = App.useApp()
  const [file, setFile] = useState<{ name: string; rows: ExpenseImportRow[]; writeKey: string } | null>(null)
  const [errors, setErrors] = useState<string[]>([]),
    [reading, setReading] = useState(false)
  const preview = useMutation(trpc.expense.previewImport.mutationOptions()),
    save = useMutation(trpc.expense.importRows.mutationOptions())
  const busy = reading || preview.isPending || save.isPending
  return (
    <Modal
      open
      title="导入支出账单"
      width="40rem"
      onCancel={busy ? undefined : onClose}
      mask={{ closable: false }}
      okText={file ? `导入 ${file.rows.length} 条` : '导入'}
      okButtonProps={{ disabled: !file || errors.length > 0 || reading || preview.isPending, loading: save.isPending }}
      cancelButtonProps={{ disabled: busy }}
      onOk={async () => {
        if (!file) {
          return
        }
        try {
          const result = await save.mutateAsync(file)
          if (result.errors.length) {
            setErrors(result.errors.map((e) => `第 ${e.row} 行：${e.message}`))
            return
          }
          await onSaved()
          onClose()
          void message.success(`已导入 ${result.count} 条支出记录`)
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '导入失败')
        }
      }}
    >
      <p className="mb-4">
        按模板填写付款主体、付款对象、业务、应付日期、金额和备注。名称需与系统一致，每次最多 500 条。
      </p>
      <div className="mb-4 flex gap-2">
        <Button
          disabled={busy}
          onClick={() => void downloadExpenseTemplate().catch((error) => message.error(error.message))}
        >
          下载模板
        </Button>
        <Upload
          accept=".xlsx"
          showUploadList={false}
          disabled={busy}
          beforeUpload={async (upload) => {
            setFile(null)
            setErrors([])
            setReading(true)
            try {
              if (upload.size > 5 * 1024 * 1024) {
                throw new Error('文件不能超过 5 MB')
              }
              const rows = await readExpenseFile(await upload.arrayBuffer()),
                input = { rows, writeKey: crypto.randomUUID() }
              const result = await preview.mutateAsync(input)
              setErrors(result.errors.map((e) => `第 ${e.row} 行：${e.message}`))
              setFile({ ...input, name: upload.name })
            } catch (error) {
              setErrors([error instanceof Error ? error.message : '文件读取失败'])
            } finally {
              setReading(false)
            }
            return Upload.LIST_IGNORE
          }}
        >
          <Button loading={reading || preview.isPending}>选择 Excel 文件</Button>
        </Upload>
      </div>
      {errors.length ? (
        <Alert
          type="error"
          showIcon
          title="请修正后重新选择文件，本次尚未导入"
          description={
            <div className="max-h-64 overflow-auto">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          }
        />
      ) : file ? (
        <Alert
          type="success"
          showIcon
          title={`${file.name}：${file.rows.length} 条记录校验通过`}
          description="点击导入后保存到支出台账，并按应付日期计入报表。"
        />
      ) : null}
    </Modal>
  )
}
